import React, { useState, useEffect, useRef } from 'react';
import { Button, Card, Typography, Space, Tag, Avatar, Row, Col, Divider, Flex, Grid, Spin, message } from 'antd';
import { EyeOutlined, EyeInvisibleOutlined, EditOutlined, PlayCircleOutlined, ArrowRightOutlined, MinusOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { ReactComponent as LiveStreamIcon } from '../assets/live-stream-icon.svg';
import { ReactComponent as InteractiveIcon } from '../assets/interactive-area-icon.svg';
import VideoSection from '../components/VideoSection';
import { useDemand } from '../context/DemandContext';
import { useDemandDataLoader } from '../hooks/demand/useDemandDataLoader';
import dayjs from 'dayjs';

const { Title, Text, Paragraph } = Typography;
const { useBreakpoint } = Grid;

// InfoCard 組件
const InfoCard = ({ title, children, screens }: {
  title: string,
  children: React.ReactNode,
  screens: any
}) => (
  <Card
    style={{
      marginBottom: 24,
      borderRadius: 8,
    }}
  >
    <div style={{
      borderBottom: '1px solid #B5BEC7',
      padding: '6px 16px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    }}>
      <Title level={4} style={{
        margin: 0,
        fontSize: screens.md ? 20 : 18,
        fontFamily: 'Noto Serif TC',
      }}>{title}</Title>
      <MinusOutlined style={{
        fontSize: screens.md ? 20 : 18,
        color: '#343330'
      }} />
    </div>
    <div style={{ padding: screens.md ? '24px 24px' : '16px 16px' }}>
      {children}
    </div>
  </Card>
);

// InfoRow 組件
const InfoRow = ({
  label,
  value,
  hasEdit,
  hasEye,
  screens,
  showPassword,
  setShowPassword
}: {
  label: string,
  value: string,
  hasEdit?: boolean,
  hasEye?: boolean,
  screens: any,
  showPassword: boolean,
  setShowPassword: (show: boolean) => void
}) => (
  <div style={{
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: screens.md ? 24 : 16
  }}>
    <label style={{
      fontSize: screens.md ? 20 : 16,
      color: '#000'
    }}>{label}</label>
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <span style={{
        fontSize: screens.md ? 20 : 16,
        color: '#000'
      }}>
        {hasEye && !showPassword ? '＊＊＊＊＊＊＊＊' : value}
      </span>
      {hasEye && (
        <Button
          type="text"
          icon={showPassword ? <EyeOutlined /> : <EyeInvisibleOutlined />}
          onClick={() => setShowPassword(!showPassword)}
          style={{
            padding: 0,
            minWidth: 'auto',
            height: screens.md ? 28 : 24,
            width: screens.md ? 28 : 24
          }}
        />
      )}
      {hasEdit && (
        <EditOutlined style={{
          fontSize: screens.md ? 20 : 16,
          color: '#343330',
          cursor: 'pointer'
        }} />
      )}
    </div>
  </div>
);

// ActionButton 組件
const ActionButton = ({
  title,
  icon,
  onClick
}: {
  title: string,
  icon: React.ReactNode,
  onClick?: () => void
}) => (
  <Card
    hoverable
    onClick={onClick}
    style={{
      background: 'linear-gradient(90deg, #FFFFFF 0%, #FFDD96 62.98%, #FFC74E 100%)',
      border: '1px solid #C2C2C2',
      borderRadius: 16,
      height: 243,
      position: 'relative',
      cursor: 'pointer',
      marginBottom: 24,
      overflow: 'hidden'
    }}
  >
    {/* 文字區域 - 左上角 */}
    <div style={{
      position: 'absolute',
      top: 40,
      left: 47,
      zIndex: 2
    }}>
      <Title level={3} style={{
        margin: 0,
        fontSize: 24,
        fontWeight: 800,
        color: '#3E3D3D',
        fontFamily: 'Noto Serif TC',
        letterSpacing: '0.05em'
      }}>
        {title}
      </Title>
    </div>

    {/* 圖標區域 - 右半邊填滿 */}
    <div style={{
      position: 'absolute',
      top: 0,
      right: 0,
      width: '50%',
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1,
      overflow: 'hidden'
    }}>
      <div style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#FFBC2D'
      }}>
        {React.cloneElement(icon as React.ReactElement, {
          style: {
            width: '100%',
            height: '100%',
            fill: '#FFBC2D',
            objectFit: 'cover'
          }
        })}
      </div>
    </div>

    {/* 箭頭圖標 - 左下角 */}
    <div style={{
      position: 'absolute',
      bottom: 47,
      left: 47,
      zIndex: 2
    }}>
      <ArrowRightOutlined style={{
        fontSize: 32,
        color: '#343330'
      }} />
    </div>
  </Card>
);

const Member: React.FC = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const hasLoadedRef = useRef(false); // 追蹤是否已經載入過

  // 響應式斷點
  const screens = useBreakpoint();

  // Context 和 Hook
  const { state } = useDemand();
  const { isLoading, error, loadCurrentMemberData } = useDemandDataLoader();

  // 數據載入邏輯
  useEffect(() => {
    const loadData = async () => {
      // 如果已經載入過，就不再載入
      if (hasLoadedRef.current) {
        return;
      }

      // 從 localStorage 獲取會員資料
      const memberInfo = JSON.parse(localStorage.getItem('memberInfo') || '{}');
      
      if (memberInfo.projectName) {
        hasLoadedRef.current = true; // 標記為已載入
        try {
          const success = await loadCurrentMemberData();
          if (!success) {
            console.error('載入需求單數據失敗');
            hasLoadedRef.current = false; // 載入失敗時重置標記
          }
        } catch (error) {
          console.error('載入需求單數據發生異常:', error);
          hasLoadedRef.current = false; // 發生異常時重置標記
        }
      } else {
        message.error('未找到會員資訊');
        navigate('/');
      }
    };
    
    loadData();
  }, []); // 空依賴項，只在組件掛載時執行

  // 從 localStorage 獲取會員資料（用於顯示）
  const memberInfo = JSON.parse(localStorage.getItem('memberInfo') || '{}');

  // 從 Context 獲取實際數據
  const memberData = {
    name: state.basic?.familyName || 'ＯＯＯ', // 使用家屬姓名作為用戶名稱
    friendName: state.basic?.name || 'ＯＯＯ', // 親友姓名
    ceremonyDate: state.basic?.funeralDate
      ? dayjs(state.basic.funeralDate).format('YYYY-MM-DD')
      : '待確認',
    memberName: state.basic?.familyName || 'ＯＯＯ',
    phone: state.basic?.familyPhone || '待確認',
    orderNumber: memberInfo.projectName || '待確認'
  };

  return (
    <Spin spinning={isLoading} tip="載入需求單數據中...">
      <div
        style={{
          fontFamily: 'Noto Serif TC',
        }}
      >
        {/* 用戶問候語 */}
        <Flex vertical className='md:px-[100px] md:py-[50px] w-full p-2 gap-4 bg-white'>
          <Flex align="center">
            <Title
              level={2}
              style={{
                margin: 0,
                fontSize: screens.md ? 32 : 24,  // 手機版 24px，桌面版 32px
                fontWeight: 700,
                letterSpacing: '0.1em',
                color: '#3E3D3D',
                fontFamily: 'Noto Serif TC',
              }}
            >
              {memberData.name}，您好
            </Title>
          </Flex>

          <Row justify="center" className='mb-10'>
            <Col span={24}>
              <InfoCard title="告別式資訊" screens={screens}>
                <InfoRow
                  label="需求單編號"
                  value={memberData.orderNumber}
                  screens={screens}
                  showPassword={showPassword}
                  setShowPassword={setShowPassword}
                />
                <InfoRow
                  label="親友姓名"
                  value={memberData.friendName}
                  screens={screens}
                  showPassword={showPassword}
                  setShowPassword={setShowPassword}
                />
                <InfoRow
                  label="告別式日期"
                  value={memberData.ceremonyDate}
                  screens={screens}
                  showPassword={showPassword}
                  setShowPassword={setShowPassword}
                />

                <Button
                  type="default"
                  size="large"
                  block
                  onClick={() => navigate(`/combo?id=${memberData.orderNumber}`)}
                  style={{
                    borderColor: '#FFB81F',
                    color: '#FFB81F',
                    fontSize: screens.md ? 16 : 14,  // 手機版 14px，桌面版 16px
                    fontWeight: 600,
                    height: screens.md ? 48 : 40,  // 手機版高度也縮小
                    marginTop: 16
                  }}
                >
                  查看需求單
                </Button>
              </InfoCard>
            </Col>
          </Row>

          <Row gutter={[{ xs: 0, md: 24 }, { xs: 0, md: 24 }]}>
            <Col xs={24} lg={12}>
              <ActionButton
                title="前往告別式直播"
                icon={<LiveStreamIcon />}
                onClick={() => navigate('/livestream')}
              />
            </Col>

            <Col xs={24} lg={12}>
              <ActionButton
                title="前往互動專區"
                icon={<InteractiveIcon />}
                onClick={() => navigate('/interactive')}
              />
            </Col>
          </Row>
        </Flex>

        <VideoSection />
      </div>
    </Spin>
  );
};

export default Member;